/* eslint-disable no-unused-vars, jsx-a11y/alt-text, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
    FiHome,
    FiUsers,
    FiShoppingBag,
    FiSettings,
    FiLogOut,
    FiMenu,
    FiCheckSquare,
    FiSearch,
    FiBell,
    FiFileText,
    FiAward,
    FiZap,
    FiActivity,
    FiServer,
    FiKey,
    FiDatabase,
    FiSun,
    FiMoon,
    FiTrendingDown,
    FiSliders,
    FiHeart,
    FiShield
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import '@fontsource/cairo';

const AdminSidebar = ({ isOpen, setIsOpen }) => {
    const { user, logout } = useAuth();
    const [logoUrl, setLogoUrl] = useState('/logo-brand.jpg');

    useEffect(() => {
        const savedLogo = localStorage.getItem('system_logo_url');
        if (savedLogo) setLogoUrl(savedLogo);
    }, []);

    const navLinks = [
        { name: 'لوحة التحكم', icon: <FiHome />, to: '/admin/dashboard' },
        { name: 'إدارة المطاعم', icon: <FiShoppingBag />, to: '/admin/restaurants' },
        { name: 'إدارة المستخدمين', icon: <FiUsers />, to: '/admin/users' },
        { name: 'الباقات والاشتراكات', icon: <FiCheckSquare />, to: '/admin/plans' },
        { name: 'إدارة الطلبات', icon: <FiSettings />, to: '/admin/orders' },
        { name: 'عروض الأسعار والصيانة', icon: <FiFileText />, to: '/admin/quotations' },
        { name: 'الموزعين المعتمدين', icon: <FiAward />, to: '/admin/distributors' },
        { name: 'مختبر الأفكار', icon: <FiZap />, to: '/admin/ideas' },
        { name: 'الخسائر والتطوير', icon: <FiTrendingDown />, to: '/admin/loss-development' },
        { name: 'مركز التحكم المركزي', icon: <FiSliders />, to: '/admin/system-control' },
        { name: 'سجل الأمان والعمليات', icon: <FiShield />, to: '/admin/security-logs' },
        { name: 'النظام الصحي للخدمة', icon: <FiHeart />, to: '/admin/system-health' },
        { name: 'مفاتيح الربط API', icon: <FiKey />, to: '/admin/api-keys' },
        { name: 'نشاط المستخدمين والذكاء', icon: <FiActivity />, to: '/admin/user-activity' },
        { name: 'السيادة والامتثال', icon: <FiDatabase />, to: '/admin/maintenance-sovereignty' }
    ];

    return (
        <aside
            className={`fixed inset-y-0 right-0 z-40 w-72 bg-slate-900 text-white shadow-2xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
            <div className="flex flex-col h-full">
                {/* Brand */}
                <div className="flex items-center h-20 px-8 border-b border-white/5">
                    <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="w-10 h-10 rounded-xl object-cover shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 hover:rotate-3 hover:brightness-110 hover:shadow-emerald-500/20 hover:shadow-xl cursor-pointer" 
                    />
                    <div className="mr-3">
                        <h1 className="font-black text-lg tracking-tight text-white">DFOOD</h1>
                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest">
                            Admin Panel
                        </p>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-4 py-8 space-y-2 overflow-y-auto custom-scrollbar">
                    {navLinks.map((link) => (
                        <NavLink
                            key={link.name}
                            to={link.to}
                            className={({ isActive }) =>
                                `group flex items-center px-4 py-3.5 text-sm font-bold rounded-xl transition-all duration-200 relative overflow-hidden ${isActive
                                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-900/20'
                                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                                }`
                            }
                        >
                            <span className="text-xl relative z-10">{link.icon}</span>
                            <span className="mr-3 relative z-10">{link.name}</span>
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-indigo-600/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </NavLink>
                    ))}
                </nav>

                {/* User Profile */}
                <div className="p-4 mx-4 mb-4 bg-white/5 rounded-2xl border border-white/5 backdrop-blur-sm">
                    <div className="flex items-center mb-4">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white shadow-inner">
                            <span className="font-bold text-sm">{user?.name?.charAt(0)}</span>
                        </div>
                        <div className="mr-3 overflow-hidden">
                            <p className="text-sm font-bold text-white truncate">{user?.name}</p>
                            <p className="text-[10px] text-slate-400 truncate font-medium">
                                {user?.email}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        className="w-full flex items-center justify-center px-4 py-2.5 text-xs font-bold rounded-xl text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 transition-all border border-rose-500/20 group"
                    >
                        <FiLogOut className="ml-2 group-hover:-translate-x-1 transition-transform" />
                        تسجيل الخروج
                    </button>
                </div>
            </div>
        </aside>
    );
};

const AdminLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { darkMode, toggleDarkMode, themeMode, setThemeMode, toggleTheme } = useTheme();
    const [scrolled, setScrolled] = useState(false);

    // Default to Classic for Admin Dashboard if no theme is saved
    useEffect(() => {
        if (!localStorage.getItem('themeMode')) {
            setThemeMode('classic');
        }
    }, [setThemeMode]);

    const handleScroll = (e) => {
        setScrolled(e.target.scrollTop > 10);
    };

    return (
        <div className={`min-h-screen flex font-cairo ${darkMode ? 'dark' : ''} ${themeMode === 'classic' ? 'theme-classic' : 'theme-premium'}`} dir="rtl">
            <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

            <div className="flex-1 flex flex-col overflow-hidden h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
                {/* Header */}
                <header
                    className={`bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-30 transition-all duration-200 ${scrolled ? 'shadow-sm border-b border-slate-200/50 dark:border-slate-800/50' : ''}`}
                >
                    <div className="flex items-center justify-between h-20 px-6 lg:px-10">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="text-slate-500 dark:text-slate-400 lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                            >
                                <FiMenu size={24} />
                            </button>

                            {/* Search Bar - Desktop */}
                            <div className="hidden md:flex items-center relative w-96">
                                <FiSearch className="absolute right-4 text-slate-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="بحث في النظام..."
                                    className="w-full bg-slate-100/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl py-3 pr-11 pl-4 text-sm font-bold text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-purple-500/10 dark:focus:ring-purple-500/5 focus:border-purple-500 transition-all placeholder:text-slate-400"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-4">
                            <button className="p-3 text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-500/10 rounded-xl transition-all relative">
                                <FiBell size={20} />
                                <span className="absolute top-3 right-3 w-2 h-2 bg-rose-500 rounded-full border-2 border-white dark:border-slate-900"></span>
                            </button>

                            <div className="h-8 w-px bg-slate-200 dark:bg-slate-800 mx-1"></div>

                            <button
                                onClick={toggleDarkMode}
                                className="p-3 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-xl transition-all"
                                title={darkMode ? 'الوضع الفاتح' : 'الوضع الليلي'}
                            >
                                {darkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
                            </button>

                            <button
                                onClick={toggleTheme}
                                className={`p-3 rounded-xl transition-all ${themeMode === 'premium' ? 'text-purple-600 bg-purple-50 dark:bg-purple-900/20' :
                                    themeMode === 'luxury' ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' :
                                        'text-slate-400 hover:text-slate-600'
                                    }`}
                                title={
                                    themeMode === 'premium' ? 'تبديل للثيم السابق' :
                                        themeMode === 'luxury' ? 'تبديل للثيم الفاخر' :
                                            'تبديل للثيم الكلاسيكي'
                                }
                            >
                                🎨
                            </button>
                            <Link
                                to="/"
                                className="hidden sm:flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-xl transition-colors"
                            >
                                عرض الموقع
                                <FiCheckSquare className="text-lg" />
                            </Link>
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-1 overflow-y-auto scroll-smooth" onScroll={handleScroll}>
                    <div className="w-full h-full py-8 px-4 sm:px-6 lg:px-8">
                        <Outlet />
                    </div>
                </main>
            </div>

            {/* Mobile Sidebar Overlay */}
            <AnimatePresence>
                {sidebarOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSidebarOpen(false)}
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-30 lg:hidden"
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminLayout;
